import i18n, {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  getLocaleFromPath,
  type SupportedLocale,
} from '@/i18n';
import {
  safeLocalStorageGetItem,
  safeLocalStorageSetItem,
} from '@/utils/safeLocalStorage';

const STORAGE_KEY = 'indmo-preferred-ui-locale';

function isSupportedLocale(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/** Persisted UI language (vue-i18n) when the URL is default-locale (unprefixed). */
export function readPreferredUiLocale(): SupportedLocale | null {
  const raw = safeLocalStorageGetItem(STORAGE_KEY);
  if (!raw || !isSupportedLocale(raw)) return null;
  return raw;
}

export function writePreferredUiLocale(locale: SupportedLocale): void {
  safeLocalStorageSetItem(STORAGE_KEY, locale);
}

/** When the URL has an explicit /en/ or /pt/ prefix, treat it as the user's choice and persist it. */
export function persistPrefixedPathLocale(pathname: string): void {
  const pathLocale = getLocaleFromPath(pathname);
  if (pathLocale !== DEFAULT_LOCALE) {
    writePreferredUiLocale(pathLocale);
  }
}

/**
 * Locale for vue-i18n and localized chrome: URL prefix wins; on default-locale URLs use
 * stored preference so Tetum-only pages do not reset UI from en/pt.
 */
export function resolveUiLocaleForPath(pathname: string): SupportedLocale {
  const pathLocale = getLocaleFromPath(pathname);
  if (pathLocale !== DEFAULT_LOCALE) return pathLocale;
  const stored = readPreferredUiLocale();
  if (stored && stored !== DEFAULT_LOCALE) return stored;
  return DEFAULT_LOCALE;
}

/**
 * Re-read preference + path and sync vue-i18n / `document.documentElement.lang`.
 * Call after updating preference when the router URL does not change (no `afterEach`).
 */
export function applyI18nLocaleForCurrentPath(pathname: string): void {
  persistPrefixedPathLocale(pathname);
  const uiLocale = resolveUiLocaleForPath(pathname);
  if (i18n.global.locale.value !== uiLocale) {
    i18n.global.locale.value = uiLocale;
  }
  document.documentElement.lang = getLocaleFromPath(pathname);
}
